import { addWorkspaceVideo } from "./workspaceVideo";
import { parseSiteLocale, readSiteLocale } from "@/components/site/useSiteLocale";
import type { VideoReference } from "@/data/video-search-content";
const APP_BASE_URL = (import.meta.env.VITE_APP_BASE_URL || "http://127.0.0.1:5173").replace(/\/$/, "");

export function buildAppDashboardUrl(agent?: string): string {
  const target = new URL("/dashboard", APP_BASE_URL);
  if (agent) target.searchParams.set("agent", agent);
  target.searchParams.set("lang", readSiteLocale());
  return target.href;
}

export function buildAppAuthUrl(action: "login" | "register", current: URL, video?: VideoReference): string {
  const target = new URL(`/${action}`, APP_BASE_URL);
  const locale = parseSiteLocale(current.searchParams.get("lang")) ?? readSiteLocale();
  target.searchParams.set("lang", locale);
  if (current.pathname !== "/") {
    const destination = current.pathname === "/tools/video-search" ? new URL(buildVideoWorkspaceUrl(video)) : new URL(current);
    destination.searchParams.set("lang", locale);
    // App destinations must be relative: the App only accepts its allowlisted paths.
    target.searchParams.set("return_to", destination.origin === new URL(APP_BASE_URL).origin
      ? `${destination.pathname}${destination.search}${destination.hash}` : destination.href);
  }
  return target.href;
}

/**
 * Workspace target for the video search page. Pass the reference the visitor is looking at to
 * hand that single video over; without one the composer stays empty.
 */
export function buildVideoWorkspaceUrl(video?: VideoReference): string {
  const target = new URL(buildAppDashboardUrl("creative-analyst"));
  addWorkspaceVideo(target, video);
  return target.href;
}

export function buildAppLoginUrl(authenticated: boolean, current: URL, video?: VideoReference): string {
  if (authenticated) {
    const target = new URL(current.pathname === "/tools/video-search" ? buildVideoWorkspaceUrl(video) : buildAppDashboardUrl());
    target.searchParams.set("lang", parseSiteLocale(current.searchParams.get("lang")) ?? readSiteLocale());
    return target.href;
  }
  return buildAppAuthUrl("login", current, video);
}

export function buildWorkspaceEntryUrl(authenticated: boolean, destination: string): string {
  const target = new URL(destination);
  target.searchParams.set("lang", readSiteLocale());
  return authenticated ? target.href : buildAppAuthUrl("login", target);
}

export function navigateToApp(href: string): void {
  window.location.assign(href);
}
